"use client";
import { useCallback, useEffect, useState } from "react";
import { Logo, LogoTheme } from "../logo";
import { useThrottleFn } from "@/hooks/useThrottleFn";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setHeaderTheme } from "@/store/headerSlice";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { CONVERSION_PAGE_PATH } from "@/lib/conversion-page";

export function Header({ children }: { children: Readonly<React.ReactNode> }) {
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { theme } = useAppSelector((state) => state.header);
  const [logoTheme, setLogoTheme] = useState<LogoTheme | null>(null);
  const { originalScrollY, isOverlayOpen } = useAppSelector(
    (state) => state.overlay,
  );

  const isColorDark = useCallback((color: string) => {
    const rgb = color.match(/\d+/g);
    if (!rgb) return false;
    const [r, g, b] = rgb.map(Number);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  }, []);

  const handleScroll = useCallback(() => {
    const sections = document.querySelectorAll("section, article");
    const scrollPosition = isOverlayOpen
      ? originalScrollY
      : window.scrollY + 100;

    const currentSection = Array.from(sections).find((section) => {
      const { offsetTop, offsetHeight } = section as HTMLElement;
      return (
        scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight
      );
    });

    if (currentSection) {
      const bgColor = window.getComputedStyle(currentSection).backgroundColor;
      const isDark = isColorDark(bgColor);
      const theme = isDark ? "dark" : "light";
      dispatch(setHeaderTheme(theme));
    }
  }, [dispatch, isColorDark, isOverlayOpen, originalScrollY]);

  const { run, cancel, flush } = useThrottleFn(handleScroll, 200);

  // Run initial check immediately (synchronously) before mounting
  useEffect(() => {
    handleScroll(); // Run immediately without throttle for initial render
    run(); // Also schedule throttled version
    window.addEventListener("scroll", run);
    return () => {
      window.removeEventListener("scroll", run);
      cancel();
    };
  }, [run, cancel, flush, pathname, handleScroll]);

  useEffect(() => {
    if (theme) {
      setLogoTheme(theme === "dark" ? "light" : "dark");
    }
  }, [theme]);

  return (
    <header
      className="sticky top-0 left-0 right-0 z-10 w-full max-w-[100vw] py-2 lg:py-4 font-outfit bg-transparent data-[theme=light]:bg-white data-[theme=light]:shadow-[0_8px_30px_15px_rgba(0,0,0,0.05)]"
      data-theme={theme}
    >
      <div className="max-w-screen-2xl mx-auto flex justify-between items-center px-8">
        {pathname === CONVERSION_PAGE_PATH ? (
          <Logo theme={logoTheme} />
        ) : (
          <Link href="/">
            <Logo theme={logoTheme} />
          </Link>
        )}
        {children}
      </div>
    </header>
  );
}
